import { QueryClient } from '@tanstack/react-query'
import { HexApiError } from './types'

/* Cache policy is set once, here, rather than per-hook.

   The catalog is the reason for the long `staleTime`. A provider list does not
   change between two taps, and refetching it on every back-navigation is what
   makes a PWA feel worse than the native app it is competing with. Five
   minutes of staleness is invisible to the user and removes almost every
   redundant request in a browsing session.

   `gcTime` is deliberately much longer than `staleTime`: keeping the data
   resident means going back to a list you were just on paints instantly from
   cache and then revalidates, instead of showing a spinner over content the
   user saw four seconds ago. */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      // Offline and not-found are both terminal. Retrying either wastes the
      // user's battery to arrive at the same message three seconds later.
      retry: (failureCount, error) => {
        if (error instanceof HexApiError) {
          if (error.code === 'offline' || error.code === 'not-found' || error.code === 'unauthorized')
            return false
        }
        return failureCount < 2
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // A write that failed may have succeeded server-side. Retrying a
      // placeOrder is how a customer ends up with two identical bookings.
      retry: 0,
    },
  },
})

/* Query keys in one place. A key typed inline at the call site is a cache miss
   waiting to happen — and worse, an invalidation that silently matches
   nothing. */
export const qk = {
  categories: ['categories'] as const,
  areas: ['areas'] as const,
  cities: ['cities'] as const,
  providers: (filters: unknown) => ['providers', filters] as const,
  provider: (slug: string) => ['provider', slug] as const,
  /* Keyed by list, not by provider: HexClean's own price list for a category
     is cached under the category id, a partner's under theirs, and the two
     never collide because provider ids are prefixed. */
  menu: (listId: string) => ['menu', listId] as const,
  reviews: (providerId: string) => ['reviews', providerId] as const,
  featuredReviews: ['featured-reviews'] as const,
  promos: ['promos'] as const,
  slots: (listId: string) => ['slots', listId] as const,
  /* Keyed by what actually decides the price, not by the basket.
     `CartLine` carries `key`, `listId` and `notes` as well, and none of them
     reach `domain.Price` — keying on the whole line would re-price the basket
     because somebody typed "no starch". */
  quote: (priced: unknown) => ['quote', priced] as const,
  applications: ['partner-applications'] as const,
  search: (q: string, locale: string) => ['search', q, locale] as const,
  addresses: ['addresses'] as const,
  favourites: ['favourites'] as const,
  /** The signed-in customer, re-read from the service. Not the same thing as
   *  the copy in `state/auth`, which is hydrated from storage so the first
   *  frame is correct and is exactly what goes stale. */
  me: ['me'] as const,
  orders: ['orders'] as const,
  order: (id: string) => ['order', id] as const,
}
